import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSearch, FaChevronDown, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getDiagnosticSubscribers } from '../services/subscriberWalletService';
import { useAuth } from '../auth/AuthContext';
import { formatDateTimeIST, formatSlotDateTime } from '../utils/dateFormatter';
import { DIAGNOSTIC_LIST_URL_KEY, getStatusBadgeStyle } from '../utils/subscriberHelpers';

// Currency formatter with truncation to 2 decimals (no rounding)
const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') {
    return `₹${formattedWhole}`;
  }
  return `₹${formattedWhole}.${decimalPart}`;
};


// Scroll offset saved when a row is opened, keyed by the list URL it was opened from,
// so Back lands on the same rows rather than the top of the page.
const SCROLL_KEY = 'diagnosticSubscribersList:scroll';

const DiagnosticSubscribersList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // State for API data
  const [diagnosticSubscribers, setDiagnosticSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [serviceProviderOptions, setServiceProviderOptions] = useState([]);
  
  // Search state - initialize from URL or empty
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState(() => searchParams.get('date_filter') || '');
  const [startDate, setStartDate] = useState(() => searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(() => searchParams.get('end_date') || '');
  
  // Convert string dates to Date objects for DatePicker
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;
  
  // Helper to convert Date to YYYY-MM-DD string
  const dateToString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Custom DatePicker Input Component
  const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, clearDate }, ref) => (
    <div className="relative w-full">
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm text-left hover:border-gray-400 transition-colors"
      >
        <span className={value ? 'text-gray-900 block truncate pr-8' : 'text-gray-400 block truncate pr-8'}>
          {value || placeholder}
        </span>
      </button>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
        {value && clearDate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearDate();
            }}
            className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 pointer-events-auto"
            aria-label="Clear date"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        )}
        <FaCalendarAlt className="text-gray-400 w-4 h-4 pointer-events-none" />
      </div>
    </div>
  ));
  CustomDateInput.displayName = 'CustomDateInput';
  
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const statusParam = searchParams.get('status');
    return statusParam ? statusParam.split(',') : [];
  });
  const [selectedServiceProviders, setSelectedServiceProviders] = useState(() => {
    const spParam = searchParams.get('service_provider');
    return spParam ? spParam.split(',') : [];
  });
  
  // Dropdown open states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [serviceProviderDropdownOpen, setServiceProviderDropdownOpen] = useState(false);
  
  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance', 
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    return roleMapping[authRole] || null;
  })();
  
  const PAGE_SIZE = 25;
  // Initialize page from URL or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0
  });
  
  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  // For Ops and SuperAdmin: start with masked, can toggle. For Support: always masked (no toggle)
  const [mobilesUnmasked, setMobilesUnmasked] = useState(false);
  
  // Track if initial mount is complete to prevent duplicate calls
  const isInitialMountRef = useRef(true);
  // Track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef({});
  // Track if API call is in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (page > 1) params.set('page', page.toString());
    if (search.trim()) params.set('search', search.trim());
    if (dateFilter) params.set('date_filter', dateFilter);
    if (dateFilter === 'custom' && startDate) params.set('start_date', startDate);
    if (dateFilter === 'custom' && endDate) params.set('end_date', endDate);
    if (selectedStatuses.length > 0) params.set('status', selectedStatuses.join(','));
    if (selectedServiceProviders.length > 0) params.set('service_provider', selectedServiceProviders.join(','));
    
    setSearchParams(params, { replace: true });
  }, [page, search, dateFilter, startDate, endDate, selectedStatuses, selectedServiceProviders, setSearchParams]);
  
  // Handle search submission (Enter key or debounce)
  const handleSearchSubmit = useCallback(() => {
    const newSearch = searchInput.trim();
    if (newSearch !== search) {
      setSearch(newSearch);
      setPage(1); // Reset to page 1 when search changes
    }
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [searchInput, search]);
  
  // Debounce search input (2 seconds)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearchSubmit();
    }, 2000);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, handleSearchSubmit]);
  
  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    const last4 = digits.slice(-4);
    return `******${last4}`;
  };
  
  // Load diagnostic subscribers from API
  useEffect(() => {
    // Skip on initial mount if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Skip if not authenticated or no role
    if (!isAuthenticated || !currentRole) {
      return;
    }
    
    // Build API params
    const apiParams = {
      page: page || 1,
      items: PAGE_SIZE
    };
    
    if (search.trim()) apiParams.search = search.trim();
    if (dateFilter) {
      apiParams.date_filter = dateFilter;
      if (dateFilter === 'custom' && startDate && endDate) {
        apiParams.start_date = startDate;
        apiParams.end_date = endDate;
      }
    }
    if (selectedStatuses.length > 0) apiParams.status = selectedStatuses;
    if (selectedServiceProviders.length > 0) apiParams.service_provider = selectedServiceProviders;
    
    // Create a key for comparison
    const apiCallKey = JSON.stringify(apiParams);
    
    // Prevent duplicate API calls with same parameters
    if (lastApiCallRef.current.key === apiCallKey) {
      return;
    }
    
    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }
    
    // Update last API call reference
    lastApiCallRef.current = { key: apiCallKey, params: apiParams };
    
    const loadDiagnosticSubscribers = async () => {
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await getDiagnosticSubscribers(currentRole, apiParams);
        
        let subscribersData = null;
        let paginationData = null;
        
        // Handle response structure: { msg: { items: [...], pagination: {...}, filters: {...} }, metadata: {...} }
        if (response && response.msg) {
          if (response.msg.items && Array.isArray(response.msg.items)) {
            subscribersData = response.msg.items;
          }
          if (response.msg.pagination) {
            paginationData = response.msg.pagination;
          }
          // Extract filter options from API response
          if (response.msg.filters) {
            if (response.msg.filters.status_options && Array.isArray(response.msg.filters.status_options)) {
              setStatusOptions(response.msg.filters.status_options);
            }
            if (response.msg.filters.service_provider_options && Array.isArray(response.msg.filters.service_provider_options)) {
              setServiceProviderOptions(response.msg.filters.service_provider_options);
            }
          }
        } else if (response && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            subscribersData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
          // Extract filter options from alternative response structure
          if (response.data.filters) {
            if (response.data.filters.status_options && Array.isArray(response.data.filters.status_options)) {
              setStatusOptions(response.data.filters.status_options);
            }
            if (response.data.filters.service_provider_options && Array.isArray(response.data.filters.service_provider_options)) {
              setServiceProviderOptions(response.data.filters.service_provider_options);
            }
          }
        } else if (Array.isArray(response)) {
          subscribersData = response;
          paginationData = {
            page: 1,
            items: response.length,
            total: response.length,
            total_pages: Math.ceil(response.length / PAGE_SIZE)
          };
        }
        
        if (subscribersData && Array.isArray(subscribersData)) {
          setDiagnosticSubscribers(subscribersData);
          if (paginationData) {
            // Calculate total_pages from total and items (backend doesn't provide total_pages)
            const calculatedTotalPages = Math.ceil(paginationData.total / paginationData.items);
            setPagination({
              ...paginationData,
              total_pages: calculatedTotalPages
            });
          } else {
            setPagination({
              page: page || 1,
              items: PAGE_SIZE,
              total: subscribersData.length,
              total_pages: Math.ceil(subscribersData.length / PAGE_SIZE)
            });
          }
        } else {
          setDiagnosticSubscribers([]);
          setPagination({
            page: 1,
            items: PAGE_SIZE,
            total: 0,
            total_pages: 0
          });
          setError(`Failed to load diagnostic subscribers - Response: ${JSON.stringify(response).substring(0, 200)}...`);
        }
      } catch (error) {
        console.error('Error loading diagnostic subscribers:', error);
        setError('Failed to load diagnostic subscribers');
        setDiagnosticSubscribers([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      }
    };
    
    loadDiagnosticSubscribers();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search, dateFilter, startDate, endDate, selectedStatuses, selectedServiceProviders]);

  // Restore the scroll offset once the rows it was measured against are back.
  useEffect(() => {
    if (loading || diagnosticSubscribers.length === 0) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved) sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.search !== location.search) return;
      // The table settles over a few frames; keep nudging until the page is tall enough.
      let frames = 0;
      const restore = () => {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) > 2 && frames++ < 30) requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    } catch {
      // sessionStorage unavailable — Back still keeps page and filters via the URL.
    }
  }, [loading, diagnosticSubscribers, location.search]);

  const openBooking = (path) => {
    const from = `${location.pathname}${location.search}`;
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, y: window.scrollY }));
      sessionStorage.setItem(DIAGNOSTIC_LIST_URL_KEY, from);
    } catch {
      // ignore — scroll restore is a convenience
    }
    navigate(path, { state: { from } });
  };
  
  const handleStatusChange = (statusValue) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusValue)) {
        return prev.filter(s => s !== statusValue);
      } else {
        return [...prev, statusValue];
      }
    });
    setPage(1);
  };
  
  const handleServiceProviderChange = (spValue) => {
    setSelectedServiceProviders(prev => {
      if (prev.includes(spValue)) {
        return prev.filter(s => s !== spValue);
      } else {
        return [...prev, spValue];
      }
    });
    setPage(1);
  };
  
  const clearStatusFilters = () => {
    setSelectedStatuses([]);
    setPage(1);
  };
  
  const clearServiceProviderFilters = () => {
    setSelectedServiceProviders([]);
    setPage(1);
  };
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.status-dropdown-container')) {
        setStatusDropdownOpen(false);
      }
      if (!event.target.closest('.service-provider-dropdown-container')) {
        setServiceProviderDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setPage(1);
    if (value !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };
  
  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  if (loading && diagnosticSubscribers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error && diagnosticSubscribers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/subscriber-wallet')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="mr-2">Diagnostics Subscribers</span>
              </h2>
            </div>
            
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name, Mobile, or REF CODE..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="space-y-4">
              {/* Date Filter Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleDateFilterChange('')}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      dateFilter === ''
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                    }`}
                  >
                    All Dates
                  </button>
                  <button
                    onClick={() => handleDateFilterChange('today')}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      dateFilter === 'today'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => handleDateFilterChange('yesterday')}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      dateFilter === 'yesterday'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                    }`}
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => handleDateFilterChange('last_7_days')}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      dateFilter === 'last_7_days'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => handleDateFilterChange('custom')}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      dateFilter === 'custom'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                    }`}
                  >
                    Custom Range
                  </button>
                </div>
                
                {/* Custom Date Range */}
                {dateFilter === 'custom' && (() => {
                  // Calculate max date for end date (startDate + 31 days)
                  const getMaxEndDate = () => {
                    if (!startDateObj) return null;
                    const maxDate = new Date(startDateObj);
                    maxDate.setDate(maxDate.getDate() + 31);
                    return maxDate;
                  };

                  // Calculate min date for start date (endDate - 31 days)
                  const getMinStartDate = () => {
                    if (!endDateObj) return null;
                    const minDate = new Date(endDateObj);
                    minDate.setDate(minDate.getDate() - 31);
                    return minDate;
                  };

                  // Validate date range doesn't exceed 31 days
                  const validateDateRange = (newStart, newEnd) => {
                    if (newStart && newEnd) {
                      const diffTime = Math.abs(newEnd - newStart);
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return diffDays <= 31;
                    }
                    return true;
                  };

                  return (
                  <div className="mt-3 flex gap-3">
                      <div className="w-48">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                        <DatePicker
                          selected={startDateObj}
                          onChange={(date) => {
                            if (!date) {
                              setStartDate('');
                          setPage(1);
                              return;
                            }
                            
                            if (!endDateObj || validateDateRange(date, endDateObj)) {
                              setStartDate(dateToString(date));
                              setPage(1);
                            } else {
                              // If new start date makes range > 31 days, adjust end date
                              const maxEnd = new Date(date);
                              maxEnd.setDate(maxEnd.getDate() + 31);
                              setStartDate(dateToString(date));
                              setEndDate(dateToString(maxEnd));
                              setPage(1);
                            }
                          }}
                          selectsStart
                          startDate={startDateObj}
                          endDate={endDateObj}
                          maxDate={endDateObj || getMaxEndDate()}
                          minDate={getMinStartDate()}
                          placeholderText="Select start date"
                          dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput clearDate={() => { setStartDate(''); setPage(1); }} />}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        className="w-full"
                      />
                    </div>
                      <div className="w-48">
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                        <DatePicker
                          selected={endDateObj}
                          onChange={(date) => {
                            if (!date) {
                              setEndDate('');
                          setPage(1);
                              return;
                            }
                            
                            if (!startDateObj || validateDateRange(startDateObj, date)) {
                              setEndDate(dateToString(date));
                              setPage(1);
                            } else {
                              // If new end date makes range > 31 days, adjust start date
                              const minStart = new Date(date);
                              minStart.setDate(minStart.getDate() - 31);
                              setStartDate(dateToString(minStart));
                              setEndDate(dateToString(date));
                              setPage(1);
                            }
                          }}
                          selectsEnd
                          startDate={startDateObj}
                          endDate={endDateObj}
                          minDate={startDateObj}
                          maxDate={getMaxEndDate()}
                          placeholderText="Select end date"
                          dateFormat="dd/MM/yyyy"
                          customInput={<CustomDateInput clearDate={() => { setEndDate(''); setPage(1); }} />}
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          className="w-full"
                      />
                    </div>
                  </div>
                  );
                })()}
              </div>
              
              {/* Status and Service Provider Filters - Side by Side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Filter Dropdown */}
                <div className="status-dropdown-container">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  {selectedStatuses.length > 0 && (
                    <button
                        onClick={clearStatusFilters}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between hover:border-gray-400 transition-colors"
                    >
                      <span className="text-sm text-gray-700">
                        {selectedStatuses.length === 0
                          ? 'All Status'
                          : selectedStatuses.length === 1
                          ? statusOptions.find(opt => opt.value === selectedStatuses[0])?.label || '1 selected'
                          : `${selectedStatuses.length} selected`}
                      </span>
                      <FaChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${statusDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    
                    {statusDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {statusOptions.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">No options available</div>
                        ) : (
                          statusOptions.map(option => (
                            <label
                              key={option.value}
                              className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedStatuses.includes(option.value)}
                                onChange={() => handleStatusChange(option.value)}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{option.label}</span>
                            </label>
                          ))
                        )}
                </div>
                    )}
                  </div>
                  {selectedStatuses.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedStatuses.map(statusValue => {
                        const option = statusOptions.find(opt => opt.value === statusValue);
                        return option ? (
                          <span
                            key={statusValue}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md"
                          >
                            {option.label}
                            <button
                              type="button"
                              onClick={() => handleStatusChange(statusValue)}
                              className="ml-1.5 inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
              </div>
              
                {/* Service Provider Filter Dropdown */}
                <div className="service-provider-dropdown-container">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Service Provider</label>
                  {selectedServiceProviders.length > 0 && (
                    <button
                        onClick={clearServiceProviderFilters}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setServiceProviderDropdownOpen(!serviceProviderDropdownOpen)}
                      className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between hover:border-gray-400 transition-colors"
                    >
                      <span className="text-sm text-gray-700">
                        {selectedServiceProviders.length === 0
                          ? 'All Service Providers'
                          : selectedServiceProviders.length === 1
                          ? serviceProviderOptions.find(opt => opt.value === selectedServiceProviders[0])?.label || '1 selected'
                          : `${selectedServiceProviders.length} selected`}
                      </span>
                      <FaChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${serviceProviderDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    
                    {serviceProviderDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {serviceProviderOptions.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">No options available</div>
                        ) : (
                          serviceProviderOptions.map(option => (
                            <label
                              key={option.value}
                              className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedServiceProviders.includes(option.value)}
                                onChange={() => handleServiceProviderChange(option.value)}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{option.label}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {selectedServiceProviders.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedServiceProviders.map(spValue => {
                        const option = serviceProviderOptions.find(opt => opt.value === spValue);
                        return option ? (
                          <span
                            key={spValue}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md"
                          >
                            {option.label}
                            <button
                              type="button"
                              onClick={() => handleServiceProviderChange(spValue)}
                              className="ml-1.5 inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <span>Mobile</span>
                      {isOpsOrSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => setMobilesUnmasked((v) => !v)}
                          className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                          title={mobilesUnmasked ? 'Mask all mobile numbers' : 'Unmask all mobile numbers'}
                        >
                          {mobilesUnmasked ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REF CODE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cashback</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WB</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {diagnosticSubscribers.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No diagnostic subscribers found'}
                    </td>
                  </tr>
                ) : (
                  diagnosticSubscribers.map((item) => {
                    const mobileDisplay = (currentRole === 'Support' || !mobilesUnmasked)
                      ? maskMobile(item.mobile_number)
                      : (item.mobile_number || 'N/A');
                    
                    return (
                      <tr 
                        key={item.booking_id} 
                        className="hover:bg-gray-50 cursor-pointer" 
                        onClick={() => openBooking(`/subscriber-wallet/diagnostic-booking/${encodeURIComponent(item.application_id)}?booking_id=${encodeURIComponent(item.booking_id)}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateTimeIST(item.date_time)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.subscriber_name || 'N/A'}
                        </td>
                        {/* Scheduled sample-collection slot, in the same "DD MMM YYYY hh:mm A"
                            format as the Date & Time column. Absent for bookings that never
                            reached slot selection (initiated / failed). */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatSlotDateTime(item.appointment_date, item.appointment_time) || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {mobileDisplay}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.ref_code || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.service_provider || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{(item.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.payment_method || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {currencyRounded(item.cashback || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {currencyRounded(item.wallet_balance || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.credit_balance != null ? currencyRounded(item.credit_balance) : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border"
                            style={getStatusBadgeStyle(item.status)}
                          >
                            {item.status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.items) + 1}-{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Prev
                </button>
                {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                  const pageNum = i + 1;
                  const totalPages = pagination.total_pages || 1;
                  const shouldShow = 
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                  
                  if (!shouldShow) {
                    if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                      return (
                        <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticSubscribersList;

